import React, { useState, useEffect } from 'react';
import useToast from '../../hooks/useToast';
import Toast from '../../components/Toast';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    IconButton,
    Pagination,
    Breadcrumbs,
    Link as MuiLink,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    Alert,
    TextField
} from '@mui/material';
import { Print as PrintIcon, Visibility as ViewIcon, Cancel as CancelIcon, Add as AddIcon, LocalShipping as PickupIcon } from '@mui/icons-material';
import GenericModal from '../../components/GenericModal';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import {
    getPickupSheets,
    getCustomers,
    getReadyConsignments,
    createPickupSheet,
    cancelPickupSheet,
    printPickupSheet,
    markAsPickedUp,
    PickupSheet,
    Customer,
    ReadyConsignment
} from '../../services/pickupSheetApi';

const PickupSheets = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const user = useSelector((state: RootState) => state.auth.user);
    const [pickupSheets, setPickupSheets] = useState<PickupSheet[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [customerFilter, setCustomerFilter] = useState<string>('');

    // Create pickup sheet modal state
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [readyConsignments, setReadyConsignments] = useState<ReadyConsignment[]>([]);
    const [selectedConsignments, setSelectedConsignments] = useState<number[]>([]);
    const [loadingConsignments, setLoadingConsignments] = useState(false);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Rider PIN modal state
    const [riderPinModalOpen, setRiderPinModalOpen] = useState(false);
    const [selectedPickupSheetId, setSelectedPickupSheetId] = useState<number | null>(null);
    const [riderPin, setRiderPin] = useState('');
    const [submittingPickup, setSubmittingPickup] = useState(false);
    const [pinError, setPinError] = useState<string | null>(null);

    // Cancel confirmation modal state
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [cancelSheetId, setCancelSheetId] = useState<number | null>(null);
    const [submittingCancel, setSubmittingCancel] = useState(false);

    const isClient = user?.type === 'CLIENT';
    const { toast, showSuccess, showError, hideToast } = useToast();

    useEffect(() => {
        fetchPickupSheets();
        if (!isClient) {
            fetchCustomers();
        }
    }, [page, statusFilter, customerFilter]);

    const fetchPickupSheets = async () => {
        try {
            setLoading(true);
            const filters: any = {
                page,
                per_page: 20,
            };
            if (statusFilter) filters.status = parseInt(statusFilter);
            if (customerFilter) filters.customer_id = parseInt(customerFilter);

            const response = await getPickupSheets(filters);
            setPickupSheets(response.data.pickup_sheets);
            setTotalPages(response.data.pagination?.last_page || 1);
        } catch (error: any) {
            console.error('Error fetching pickup sheets:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomers = async () => {
        try {
            const response = await getCustomers();
            setCustomers(response.data.customers || []);
        } catch (error: any) {
            console.error('Error fetching customers:', error);
        }
    };

    const handleOpenCreateModal = async () => {
        setCreateModalOpen(true);
        setLoadingConsignments(true);
        setSelectedConsignments([]);
        setError(null);
        try {
            const response = await getReadyConsignments();
            setReadyConsignments(response.data.consignments || []);
        } catch (error: any) {
            setError(error.response?.data?.message || 'Failed to load consignments');
        } finally {
            setLoadingConsignments(false);
        }
    };

    const handleCreatePickupSheet = async () => {
        if (selectedConsignments.length === 0) {
            setError('Please select at least one consignment');
            return;
        }

        setCreating(true);
        setError(null);
        try {
            await createPickupSheet(selectedConsignments);
            setCreateModalOpen(false);
            fetchPickupSheets();
            showSuccess(`Pickup sheet created with ${selectedConsignments.length} consignment${selectedConsignments.length > 1 ? 's' : ''}`, 'Pickup Sheet Created');
        } catch (error: any) {
            setError(error.response?.data?.message || 'Failed to create pickup sheet');
        } finally {
            setCreating(false);
        }
    };

    const handleCancel = (id: number) => {
        setCancelSheetId(id);
        setCancelConfirmOpen(true);
    };

    const handleConfirmCancel = async () => {
        if (!cancelSheetId) return;
        setSubmittingCancel(true);
        try {
            await cancelPickupSheet(cancelSheetId);
            setCancelConfirmOpen(false);
            showSuccess('Pickup sheet cancelled successfully!');
            fetchPickupSheets();
        } catch (error: any) {
            showError(error.response?.data?.message || 'Failed to cancel pickup sheet');
        } finally {
            setSubmittingCancel(false);
        }
    };

    const handleConsignmentSelect = (id: number) => {
        setSelectedConsignments(prev =>
            prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        if (selectedConsignments.length === readyConsignments.length) {
            setSelectedConsignments([]);
        } else {
            setSelectedConsignments(readyConsignments.map(c => c.id));
        }
    };

    const handleOpenRiderPinModal = (sheetId: number) => {
        setSelectedPickupSheetId(sheetId);
        setRiderPin('');
        setPinError(null);
        setRiderPinModalOpen(true);
    };

    const handleCloseRiderPinModal = () => {
        setRiderPinModalOpen(false);
        setSelectedPickupSheetId(null);
        setRiderPin('');
        setPinError(null);
    };

    const handleMarkAsPickedUp = async () => {
        if (!riderPin.trim()) {
            setPinError('Please enter rider PIN');
            return;
        }

        if (!selectedPickupSheetId) return;

        setSubmittingPickup(true);
        setPinError(null);
        try {
            const response = await markAsPickedUp(selectedPickupSheetId, riderPin);
            handleCloseRiderPinModal();
            fetchPickupSheets();
            showSuccess(`Pickup sheet marked as picked up by ${response.data?.rider?.name || 'rider'}`, 'Picked Up');
        } catch (error: any) {
            setPinError(error.response?.data?.message || 'Failed to mark as picked up');
        } finally {
            setSubmittingPickup(false);
        }
    };

    const getStatusColor = (statusName?: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
        if (!statusName) return 'default';
        const lower = statusName.toLowerCase();
        if (lower.includes('ready')) return 'warning';
        if (lower.includes('picked') || lower.includes('shipped')) return 'success';
        if (lower.includes('pending')) return 'info';
        if (lower.includes('cancel')) return 'error';
        return 'default';
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 3 }}>
                <MuiLink
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/dashboard')}
                    sx={{ textDecoration: 'none', color: theme.palette.text.primary }}
                >
                    Home
                </MuiLink>
                <Typography color="text.primary">Pickup Sheets</Typography>
            </Breadcrumbs>

            {/* Filters */}
            {!isClient && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Filter</Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ minWidth: 200 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Customer</InputLabel>
                                    <Select
                                        value={customerFilter}
                                        label="Customer"
                                        onChange={(e) => {
                                            setCustomerFilter(e.target.value);
                                            setPage(1);
                                        }}
                                    >
                                        <MenuItem value="">All</MenuItem>
                                        {customers.map((customer) => (
                                            <MenuItem key={customer.id} value={customer.id}>
                                                {customer.name} {customer.company_name && `(${customer.company_name})`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box sx={{ minWidth: 200 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={statusFilter}
                                        label="Status"
                                        onChange={(e) => {
                                            setStatusFilter(e.target.value);
                                            setPage(1);
                                        }}
                                    >
                                        <MenuItem value="">All</MenuItem>
                                        <MenuItem value="2">Ready for Pickup</MenuItem>
                                        <MenuItem value="4">Picked Up</MenuItem>
                                        <MenuItem value="20">Pending</MenuItem>
                                        <MenuItem value="19">Cancelled</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            Pickup Sheets
                        </Typography>
                        {isClient && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleOpenCreateModal}
                            >
                                Mark Consignments Ready for Pickup
                            </Button>
                        )}
                    </Box>

                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    {user?.type === 'ADMIN' && <TableCell>Branch</TableCell>}
                                    {!isClient && <TableCell>Client Name</TableCell>}
                                    <TableCell>Total Consignments</TableCell>
                                    {!isClient && <TableCell>Handover Count</TableCell>}
                                    <TableCell>Rider</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Created At</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={isClient ? 6 : 9} align="center">
                                            <CircularProgress size={24} />
                                        </TableCell>
                                    </TableRow>
                                ) : pickupSheets.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={isClient ? 6 : 9} align="center">
                                            No records found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    pickupSheets.map((sheet) => (
                                        <TableRow key={sheet.id} hover>
                                            <TableCell>
                                                <MuiLink
                                                    component="button"
                                                    onClick={() => navigate(`/pickup-sheets/${sheet.id}`)}
                                                    sx={{ textDecoration: 'none' }}
                                                >
                                                    {sheet.unique_id}
                                                </MuiLink>
                                            </TableCell>
                                            {user?.type === 'ADMIN' && (
                                                <TableCell>{sheet.user?.branch?.name || '-'}</TableCell>
                                            )}
                                            {!isClient && (
                                                <TableCell>{sheet.user?.name || '-'}</TableCell>
                                            )}
                                            <TableCell>{sheet.total_consignments}</TableCell>
                                            {!isClient && <TableCell>{sheet.handover_count}</TableCell>}
                                            <TableCell>{sheet.rider?.name || '-'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={sheet.status?.name || 'Unknown'}
                                                    color={getStatusColor(sheet.status?.name)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{formatDate(sheet.created_at)}</TableCell>
                                            <TableCell align="center">
                                                {sheet.status?.id !== 19 && (
                                                    <IconButton
                                                        size="small"
                                                        color="info"
                                                        title="Print"
                                                        onClick={() => printPickupSheet(sheet.id)}
                                                    >
                                                        <PrintIcon />
                                                    </IconButton>
                                                )}
                                                {(sheet.status?.id === 2 || sheet.status?.id === 20) && (
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        title="Mark as Picked Up"
                                                        onClick={() => handleOpenRiderPinModal(sheet.id)}
                                                    >
                                                        <PickupIcon />
                                                    </IconButton>
                                                )}
                                                {isClient && (sheet.status?.id === 2 || sheet.status?.id === 20) && (
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        title="Cancel"
                                                        onClick={() => handleCancel(sheet.id)}
                                                    >
                                                        <CancelIcon />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, value) => setPage(value)}
                                color="primary"
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Create Pickup Sheet Modal */}
            <Dialog
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Mark Consignments Ready for Pickup</DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {loadingConsignments ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : readyConsignments.length === 0 ? (
                        <Typography sx={{ py: 4, textAlign: 'center' }}>
                            No consignments available for pickup
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedConsignments.length === readyConsignments.length}
                                                indeterminate={
                                                    selectedConsignments.length > 0 &&
                                                    selectedConsignments.length < readyConsignments.length
                                                }
                                                onChange={handleSelectAll}
                                            />
                                        </TableCell>
                                        <TableCell>Tracking #</TableCell>
                                        <TableCell>Receiver</TableCell>
                                        <TableCell>Origin</TableCell>
                                        <TableCell>Destination</TableCell>
                                        <TableCell>COD Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {readyConsignments.map((consignment) => (
                                        <TableRow
                                            key={consignment.id}
                                            hover
                                            onClick={() => handleConsignmentSelect(consignment.id)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedConsignments.includes(consignment.id)}
                                                />
                                            </TableCell>
                                            <TableCell>{consignment.tracking_number}</TableCell>
                                            <TableCell>{consignment.receiver_name}</TableCell>
                                            <TableCell>{consignment.origin_city || '-'}</TableCell>
                                            <TableCell>{consignment.destination_city || '-'}</TableCell>
                                            <TableCell>Rs. {consignment.cod_amount?.toLocaleString() || 0}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateModalOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreatePickupSheet}
                        disabled={creating || selectedConsignments.length === 0}
                    >
                        {creating ? 'Creating...' : `Create Pickup Sheet (${selectedConsignments.length} selected)`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Rider PIN Modal */}
            <GenericModal
                open={riderPinModalOpen}
                onClose={handleCloseRiderPinModal}
                title="Enter Rider PIN"
                subtitle="Enter the rider's PIN code to mark this pickup sheet as picked up."
                maxWidth="xs"
                actions={
                    <>
                        <Button onClick={handleCloseRiderPinModal} disabled={submittingPickup}
                            sx={{ color: theme.palette.text.secondary, borderRadius: 3 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleMarkAsPickedUp}
                            disabled={submittingPickup || !riderPin.trim()}
                            sx={{ borderRadius: 3 }}
                        >
                            {submittingPickup ? 'Processing...' : 'Mark as Picked Up'}
                        </Button>
                    </>
                }
            >
                {pinError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {pinError}
                    </Alert>
                )}
                <TextField
                    autoFocus
                    fullWidth
                    label="Rider PIN"
                    type="password"
                    value={riderPin}
                    onChange={(e) => setRiderPin(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleMarkAsPickedUp();
                        }
                    }}
                    disabled={submittingPickup}
                />
            </GenericModal>

            {/* Cancel Confirmation Modal */}
            <GenericModal
                open={cancelConfirmOpen}
                onClose={() => setCancelConfirmOpen(false)}
                title="Cancel Pickup Sheet"
                maxWidth="xs"
                actions={
                    <>
                        <Button
                            onClick={() => setCancelConfirmOpen(false)}
                            disabled={submittingCancel}
                            sx={{ color: theme.palette.text.secondary, borderRadius: 3 }}
                        >
                            No, Go Back
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleConfirmCancel}
                            disabled={submittingCancel}
                            sx={{ borderRadius: 3 }}
                        >
                            {submittingCancel ? 'Processing...' : 'Yes, Cancel'}
                        </Button>
                    </>
                }
            >
                <Typography variant="body1" color="text.primary">
                    Are you sure you want to cancel this pickup sheet?
                </Typography>
            </GenericModal>

            {/* Toast Notification */}
            <Toast
                open={toast.open}
                message={toast.message}
                title={toast.title}
                severity={toast.severity}
                onClose={hideToast}
            />
        </Box>
    );
};

export default PickupSheets;
